import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import cn from 'classnames';

import { buildSeoProductUrl } from 'helpers/SeoUrlBuilder';
import ProductUtils from 'helpers/ProductUtils';
import BadgeUtils from 'helpers/BadgeUtils';
import useMartyContext from 'hooks/useMartyContext';
import { FormattedProductBundle } from 'reducers/detail/productDetail';
import { InfluencerState, InfluencerStatus } from 'types/influencer';
import ShareSocialLinks from 'components/common/ShareSocialLinks';
import { SelectedSizing } from 'types/product';
import { PRODUCT_PAGE } from 'constants/amethystPageTypes';
import { selectZsoBrandLink } from 'selectors/product';

import css from 'styles/components/productdetail/productName.scss';

interface Props {
  product: FormattedProductBundle;
  className?: string;
  colorId: string | undefined;
  brandNameClass?: string;
  productNameClass?: string;
  selectedSizing?: SelectedSizing;
  obfuscatedCustomerId?: string;
  influencer?: InfluencerState;
  showSocialLinks?: Boolean;
  isHydraMobileInfoAbove?: boolean;
  isAboveImageGallery?: boolean;
}

export const ProductNameContents = (props: Props) => {
  const {
    brandNameClass,
    className,
    colorId,
    selectedSizing,
    showSocialLinks,
    product,
    productNameClass,
    obfuscatedCustomerId,
    influencer,
    isHydraMobileInfoAbove,
    isAboveImageGallery
  } = props;
  const { brandName, productName, defaultProductType, styles, productId, defaultImageUrl, sizing } = product;
  const isNotGiftCard = !ProductUtils.isGiftCard(defaultProductType);
  const {
    testId,
    marketplace: { links, isInfluencerProgramEnabled }
  } = useMartyContext();
  const isCanonicalColor = colorId === styles[0]!.colorId;
  const urlForStructuredData = buildSeoProductUrl(product, isCanonicalColor ? undefined : colorId);
  // We keep the space after the brand name for screen readers reading the brand then the product name.

  const brandLink = useSelector(selectZsoBrandLink);
  const { fbAppId } = links.sharing;
  const { status: influencerStatus, influencerToken } = influencer || {};
  const style = ProductUtils.getStyleByColor(styles, colorId);
  const link =
    isInfluencerProgramEnabled && influencerStatus === InfluencerStatus.ACTIVE && influencerToken
      ? ProductUtils.getInfluencerSharingButtonLink(productId, influencerToken, colorId)
      : ProductUtils.getSharingButtonLink(productId, colorId);

  const linksProductContent = {
    link: link,
    name: `${brandName} ${productName}`,
    style: style.color,
    image: defaultImageUrl
  };

  const asin = ProductUtils.getSelectedStyleStockBySize([], sizing, selectedSizing, style.stocks)?.asin || null;

  const onSharingButtonClick = (linkName: string) => {
    if (isInfluencerProgramEnabled && influencerStatus === InfluencerStatus.ACTIVE && obfuscatedCustomerId && influencerToken) {
      ProductUtils.generateShareLinkAmethystEvent(linkName, obfuscatedCustomerId, productId, influencerToken, colorId);
    }
    ProductUtils.generateShareLinkEvent(linkName, asin, PRODUCT_PAGE);
  };

  const { styleId } = style;
  const shareIconProps = { fbAppId, linksProductContent, productId, styleId };

  const { badges } = style;

  const classes = [
    className,
    { [css.aboveGalleryInfo]: isHydraMobileInfoAbove && isAboveImageGallery },
    { [css.belowGalleryInfo]: isHydraMobileInfoAbove && !isAboveImageGallery }
  ];

  return (
    <div className={cn(classes)}>
      {isNotGiftCard && (
        <span className={cn(css.brandName, brandNameClass)} itemProp="brand" itemScope itemType="http://schema.org/Brand">
          <Link
            to={brandLink || ''}
            itemProp="url"
            data-test-id={testId('pdpBrandName')}
            aria-label={`${BadgeUtils.getProductNameBadgeLabel(badges)}${brandName}`}
          >
            <span itemProp="name">{brandName}</span>
          </Link>{' '}
          {showSocialLinks && <ShareSocialLinks productDetail={shareIconProps} onSharingButtonClick={onSharingButtonClick} />}
        </span>
      )}
      <span className={cn(css.productName, productNameClass)} data-test-id={testId('pdpProductName')}>
        {productName}
      </span>
      <meta itemProp="name" content={`${brandName} ${productName}`} />
      <meta itemProp="url" content={urlForStructuredData} />
    </div>
  );
};

const ProductName = ({
  className,
  colorId,
  selectedSizing,
  product,
  showSocialLinks,
  obfuscatedCustomerId,
  influencer,
  isHydraMobileInfoAbove,
  isAboveImageGallery
}: Props) => (
  <h1
    className={cn(css.container, {
      [css.breakLongProductName]: product.productName.length > 45
    })}
  >
    <ProductNameContents
      showSocialLinks={showSocialLinks}
      selectedSizing={selectedSizing}
      product={product}
      className={className}
      colorId={colorId}
      obfuscatedCustomerId={obfuscatedCustomerId}
      influencer={influencer}
      isHydraMobileInfoAbove={isHydraMobileInfoAbove}
      isAboveImageGallery={isAboveImageGallery}
    />
  </h1>
);

export default ProductName;
